import { getAIConfig } from '@gonfalon/openapi/operations/getAIConfig';
import { getAiConfigEntitlement } from '@gonfalon/openapi/operations/getAiConfigEntitlement';
import { getAIConfigQuickStats } from '@gonfalon/openapi/operations/getAIConfigQuickStats';
import { getAIConfigs, type GetAIConfigsQueryParams } from '@gonfalon/openapi/operations/getAIConfigs';
import { getAIConfigVariation } from '@gonfalon/openapi/operations/getAIConfigVariation';
import type { ModelConfig } from '@gonfalon/openapi/types';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const aiConfigs = createQueryKeys('ai-configs', {
  list: ({ projectKey, params }: { projectKey: string; params?: GetAIConfigsQueryParams }) => ({
    queryKey: [projectKey, params],
    queryFn: async ({ signal }) => reactQueryResponseAdapter(getAIConfigs({ projectKey, query: params }, { signal })),
  }),
  detail: ({ projectKey, aiConfigKey }: { projectKey: string; aiConfigKey: string }) => ({
    queryKey: [projectKey, aiConfigKey],
    queryFn: async ({ signal }) =>
      reactQueryResponseAdapter(getAIConfig({ projectKey, configKey: aiConfigKey }, { signal })),
  }),
  variation: ({
    projectKey,
    aiConfigKey,
    variationKey,
  }: {
    projectKey: string;
    aiConfigKey: string;
    variationKey: string;
  }) => ({
    queryKey: [projectKey, aiConfigKey, variationKey],
    queryFn: async ({ signal }) =>
      reactQueryResponseAdapter(getAIConfigVariation({ projectKey, configKey: aiConfigKey, variationKey }, { signal })),
  }),
});
import { bulkVersionUpdateAgentSkill } from '@gonfalon/openapi/operations/bulkVersionUpdateAgentSkill';
import { bulkVersionUpdateAITool } from '@gonfalon/openapi/operations/bulkVersionUpdateAITool';
import { bulkVersionUpdateModelConfig } from '@gonfalon/openapi/operations/bulkVersionUpdateModelConfig';
import { bulkVersionUpdatePromptSnippet } from '@gonfalon/openapi/operations/bulkVersionUpdatePromptSnippet';
import { cancelEvaluationRun } from '@gonfalon/openapi/operations/cancelEvaluationRun';
import { createEvaluationRun } from '@gonfalon/openapi/operations/createEvaluationRun';
import { deleteAgentGraph } from '@gonfalon/openapi/operations/deleteAgentGraph';
import { deleteAgentOptimization } from '@gonfalon/openapi/operations/deleteAgentOptimization';
import { deleteAgentSkill } from '@gonfalon/openapi/operations/deleteAgentSkill';
import { deleteAIConfig } from '@gonfalon/openapi/operations/deleteAIConfig';
import { deleteAIConfigTrigger } from '@gonfalon/openapi/operations/deleteAIConfigTrigger';
import { deleteAIConfigVariation } from '@gonfalon/openapi/operations/deleteAIConfigVariation';
import { deleteAITool } from '@gonfalon/openapi/operations/deleteAITool';
import { deleteModelConfig } from '@gonfalon/openapi/operations/deleteModelConfig';
import { deletePromptSnippet } from '@gonfalon/openapi/operations/deletePromptSnippet';
import { deleteRestrictedModels } from '@gonfalon/openapi/operations/deleteRestrictedModels';
import { getAgentGraph } from '@gonfalon/openapi/operations/getAgentGraph';
import { getAgentOptimization } from '@gonfalon/openapi/operations/getAgentOptimization';
import { getAgentSkill } from '@gonfalon/openapi/operations/getAgentSkill';
import { getAITool } from '@gonfalon/openapi/operations/getAITool';
import { getEvaluation } from '@gonfalon/openapi/operations/getEvaluation';
import { getEvaluationRun } from '@gonfalon/openapi/operations/getEvaluationRun';
import { getEvaluationRunSummary } from '@gonfalon/openapi/operations/getEvaluationRunSummary';
import { getModelConfig } from '@gonfalon/openapi/operations/getModelConfig';
import { getPlayground } from '@gonfalon/openapi/operations/getPlayground';
import { getPromptSnippet } from '@gonfalon/openapi/operations/getPromptSnippet';
import { internalGetAIConfig } from '@gonfalon/openapi/operations/internalGetAIConfig';
import { internalListAIConfigReferences } from '@gonfalon/openapi/operations/internalListAIConfigReferences';
import { internalListModelConfigReferences } from '@gonfalon/openapi/operations/internalListModelConfigReferences';
import { listAgentGraphs } from '@gonfalon/openapi/operations/listAgentGraphs';
import { listAgentOptimizationResults } from '@gonfalon/openapi/operations/listAgentOptimizationResults';
import { listAgentOptimizationResultsByRunId } from '@gonfalon/openapi/operations/listAgentOptimizationResultsByRunId';
import { listAgentOptimizationRuns } from '@gonfalon/openapi/operations/listAgentOptimizationRuns';
import { listAgentOptimizations } from '@gonfalon/openapi/operations/listAgentOptimizations';
import { listAgentSkillReferences } from '@gonfalon/openapi/operations/listAgentSkillReferences';
import { listAgentSkills } from '@gonfalon/openapi/operations/listAgentSkills';
import { listAgentSkillVersions } from '@gonfalon/openapi/operations/listAgentSkillVersions';
import { listAIConfigTriggers } from '@gonfalon/openapi/operations/listAIConfigTriggers';
import { listAIEvaluationProviders } from '@gonfalon/openapi/operations/listAIEvaluationProviders';
import { listAIToolReferences } from '@gonfalon/openapi/operations/listAIToolReferences';
import { listAITools } from '@gonfalon/openapi/operations/listAITools';
import { listAIToolVersions } from '@gonfalon/openapi/operations/listAIToolVersions';
import { listAllAgentOptimizationResults } from '@gonfalon/openapi/operations/listAllAgentOptimizationResults';
import { listEvaluationRunResults } from '@gonfalon/openapi/operations/listEvaluationRunResults';
import { listEvaluationRuns } from '@gonfalon/openapi/operations/listEvaluationRuns';
import { listEvaluations } from '@gonfalon/openapi/operations/listEvaluations';
import { listModelConfigs } from '@gonfalon/openapi/operations/listModelConfigs';
import { listModelConfigVersions } from '@gonfalon/openapi/operations/listModelConfigVersions';
import { listPlaygrounds } from '@gonfalon/openapi/operations/listPlaygrounds';
import { listPromptSnippetReferences } from '@gonfalon/openapi/operations/listPromptSnippetReferences';
import { listPromptSnippets } from '@gonfalon/openapi/operations/listPromptSnippets';
import { listPromptSnippetVersions } from '@gonfalon/openapi/operations/listPromptSnippetVersions';
import { listRuns } from '@gonfalon/openapi/operations/listRuns';
import { patchAgentGraph } from '@gonfalon/openapi/operations/patchAgentGraph';
import { patchAgentOptimization } from '@gonfalon/openapi/operations/patchAgentOptimization';
import { patchAgentOptimizationResult } from '@gonfalon/openapi/operations/patchAgentOptimizationResult';
import { patchAgentSkill } from '@gonfalon/openapi/operations/patchAgentSkill';
import { patchAIConfig } from '@gonfalon/openapi/operations/patchAIConfig';
import { patchAIConfigTargeting } from '@gonfalon/openapi/operations/patchAIConfigTargeting';
import { patchAIConfigVariation } from '@gonfalon/openapi/operations/patchAIConfigVariation';
import { patchAIConfigVariationsBulk } from '@gonfalon/openapi/operations/patchAIConfigVariationsBulk';
import { patchAITool } from '@gonfalon/openapi/operations/patchAITool';
import { patchEvaluation } from '@gonfalon/openapi/operations/patchEvaluation';
import { patchModelConfig } from '@gonfalon/openapi/operations/patchModelConfig';
import { patchPlayground } from '@gonfalon/openapi/operations/patchPlayground';
import { patchPromptSnippet } from '@gonfalon/openapi/operations/patchPromptSnippet';
import { postAgentGraph } from '@gonfalon/openapi/operations/postAgentGraph';
import { postAgentOptimization } from '@gonfalon/openapi/operations/postAgentOptimization';
import { postAgentSkill } from '@gonfalon/openapi/operations/postAgentSkill';
import { postAIConfig } from '@gonfalon/openapi/operations/postAIConfig';
import { postAIConfigTrigger } from '@gonfalon/openapi/operations/postAIConfigTrigger';
import { postAIConfigVariation } from '@gonfalon/openapi/operations/postAIConfigVariation';
import { postAIConfigVariationsBulk } from '@gonfalon/openapi/operations/postAIConfigVariationsBulk';
import { postAITool } from '@gonfalon/openapi/operations/postAITool';
import { postEvaluation } from '@gonfalon/openapi/operations/postEvaluation';
import { postModelConfig } from '@gonfalon/openapi/operations/postModelConfig';
import { postPlayground } from '@gonfalon/openapi/operations/postPlayground';
import { postPromptSnippet } from '@gonfalon/openapi/operations/postPromptSnippet';
import { postRestrictedModels } from '@gonfalon/openapi/operations/postRestrictedModels';
import { previewDatasetRows } from '@gonfalon/openapi/operations/previewDatasetRows';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryHook } from '../internal/createQueryHook';
import { createQueryOptions } from '../internal/createQueryOptions';

// fetch a list of AI Configs for dashboard
export const getAiConfigsQuery = createQueryOptions(getAIConfigs);
export const getInfiniteAiConfigsQuery = createInfiniteQueryOptions(getAIConfigs);
// fetch all variations of a single AI Config for detail page. Render models and prompts
export const getAiConfigQuery = createQueryOptions(getAIConfig);
// internal query to fetch shadow flag id
export const getAiConfigInternalQuery = createQueryOptions(internalGetAIConfig);
// fetch a single variation & its history
export const getAiConfigVariationQuery = createQueryOptions(getAIConfigVariation);
export const getAiConfigEntitlementQuery = createQueryOptions(getAiConfigEntitlement);
export const getAiConfigTriggersQuery = createQueryOptions(listAIConfigTriggers);

/**
 * @deprecated Use `getAiConfigsQuery()` with `useQuery` directly.
 */
export const useGetAiConfigs = createQueryHook(getAiConfigsQuery);

// create an AI Config
export const useCreateAiConfig = createMutationHook(postAIConfig, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getAiConfigsQuery.partialQueryKey({ projectKey: variables.projectKey }),
    });
  },
});
// create a new variation (modal & prompt) in an AI Config.
export const usePostVariationAiConfig = createMutationHook(postAIConfigVariation);
export const usePostVariationAiConfigBulk = createMutationHook(postAIConfigVariationsBulk, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    void queryClient.invalidateQueries({
      queryKey: getAiConfigsQuery.partialQueryKey({ projectKey: variables.projectKey }),
    });
    // Judge attachments are (de)attached via variation writes, so refresh judge "used by" counts.
    await queryClient.invalidateQueries({ queryKey: internalListAIConfigReferencesQuery.partialQueryKey() });
  },
});

// update the selected variation (modal & prompt) in an AI Config. Fetch latest variation afer update
export const useUpdateAiConfigVariation = createMutationHook(patchAIConfigVariation);
export const useUpdateAiConfigVariationBulk = createMutationHook(patchAIConfigVariationsBulk, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    void queryClient.invalidateQueries({
      queryKey: getAiConfigsQuery.partialQueryKey({ projectKey: variables.projectKey }),
    });
    // Judge attachments are (de)attached via variation writes, so refresh judge "used by" counts.
    await queryClient.invalidateQueries({ queryKey: internalListAIConfigReferencesQuery.partialQueryKey() });
  },
});

// update the targeting for an AI Config
export const useUpdateAiConfigTargeting = createMutationHook(patchAIConfigTargeting);

export const usePostAiConfigTrigger = createMutationHook(postAIConfigTrigger, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getAiConfigTriggersQuery.partialQueryKey({
        projectKey: variables.projectKey,
        configKey: variables.configKey,
      }),
    });
  },
});

/** Deletes an AI config adaptive trigger by id and refreshes the triggers list query for that config. */
export const useDeleteAiConfigTrigger = createMutationHook(deleteAIConfigTrigger, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getAiConfigTriggersQuery.partialQueryKey({
        projectKey: variables.projectKey,
        configKey: variables.configKey,
      }),
    });
  },
});

// update an AI Config. Fetch latest variation afer update
export const useUpdateAiConfig = createMutationHook(patchAIConfig, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAiConfigQuery.partialQueryKey() });
  },
});
// For use when we don't want to revalidate the AI config/wont cause a page refresh
export const useUpdateAiConfigWithoutRevalidation = createMutationHook(patchAIConfig);

// delete all ai config variations. Basically delete everything. Refetch a list of AI configs
export const useDeleteAiConfig = createMutationHook(deleteAIConfig, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.refetchQueries({ queryKey: getAiConfigsQuery.partialQueryKey() });
    // Deleting a config that attaches a judge changes that judge's references, so refresh the
    // "used by" counts for all judge configs (we don't know which judge keys were attached here).
    await queryClient.invalidateQueries({ queryKey: internalListAIConfigReferencesQuery.partialQueryKey() });
  },
});

// delete a single ai config variation (model & prompt). Retain any existing variation (model & prompts). Refetch vall there AI Config variations
export const useDeleteAiConfigVariation = createMutationHook(deleteAIConfigVariation, {
  onSuccess: async (data, { configKey }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAiConfigQuery.partialQueryKey({ configKey }) });
  },
});

// Model configs
export const getModelConfigsQuery = createQueryOptions(listModelConfigs);
export const getAIConfigQuickStatsQuery = createQueryOptions(getAIConfigQuickStats);
export const getModelConfigQuery = createQueryOptions(getModelConfig);
export const internalListModelConfigReferencesQuery = createQueryOptions(internalListModelConfigReferences);
export const internalListModelConfigReferencesInfiniteQuery = createInfiniteQueryOptions(
  internalListModelConfigReferences,
);
export const listModelConfigVersionsQuery = createQueryOptions(listModelConfigVersions);

/**
 * @deprecated Use `getModelConfigsQuery()` with `useQuery` directly.
 */
export const useGetModelConfigs = createQueryHook(getModelConfigsQuery);

export const useCreateModelConfig = createMutationHook(postModelConfig, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getModelConfigsQuery.partialQueryKey() });
  },
});

export const usePatchModelConfig = createMutationHook(patchModelConfig, {
  onSuccess: async (modelConfig) => {
    const queryClient = getQueryClient();
    queryClient.setQueriesData<ModelConfig[]>({ queryKey: getModelConfigsQuery.partialQueryKey() }, (modelConfigs) =>
      modelConfigs?.map((existingModelConfig) =>
        existingModelConfig.key === modelConfig.key ? modelConfig : existingModelConfig,
      ),
    );
    queryClient.setQueriesData<ModelConfig>(
      { queryKey: getModelConfigQuery.partialQueryKey() },
      (existingModelConfig) => (existingModelConfig?.key === modelConfig.key ? modelConfig : existingModelConfig),
    );
    await queryClient.invalidateQueries({ queryKey: getModelConfigsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: getModelConfigQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listModelConfigVersionsQuery.partialQueryKey() });
  },
});

export const useDeleteModelConfig = createMutationHook(deleteModelConfig, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getModelConfigsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: getModelConfigQuery.partialQueryKey() });
  },
});

export const useCreateRestrictedModels = createMutationHook(postRestrictedModels, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getModelConfigsQuery.partialQueryKey() });
  },
});

export const useRemoveRestrictedModels = createMutationHook(deleteRestrictedModels, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getModelConfigsQuery.partialQueryKey() });
  },
});

export const useUpdateModelConfigUsagesBulk = createMutationHook(bulkVersionUpdateModelConfig, {
  onSuccess: async (data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey, modelConfigKey } = variables;
    const results = data?.results ?? [];
    const configKeys = new Set<string>();
    for (const row of results) {
      if (row.configKey) {
        configKeys.add(row.configKey);
      }
    }
    const affectedModelConfigKeys = new Set([modelConfigKey]);
    if (variables.body.toModelConfigKey) {
      affectedModelConfigKeys.add(variables.body.toModelConfigKey);
    }
    const invalidationTasks: Array<Promise<void>> = [];
    for (const affectedModelConfigKey of affectedModelConfigKeys) {
      invalidationTasks.push(
        queryClient.invalidateQueries({
          queryKey: internalListModelConfigReferencesQuery.partialQueryKey({
            projectKey,
            modelConfigKey: affectedModelConfigKey,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: internalListModelConfigReferencesInfiniteQuery.partialQueryKey({
            projectKey,
            modelConfigKey: affectedModelConfigKey,
          }),
        }),
      );
    }
    for (const configKey of configKeys) {
      invalidationTasks.push(
        queryClient.invalidateQueries({
          queryKey: getAiConfigQuery.partialQueryKey({ projectKey, configKey }),
        }),
      );
    }
    await Promise.all(invalidationTasks);
  },
});

// Tools
export const useTools = createQueryOptions(listAITools);
export const getToolsQuery = createQueryOptions(getAITool);

export const useCreateTool = createMutationHook(postAITool, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: useTools.partialQueryKey() });
  },
});

export const useDeleteTool = createMutationHook(deleteAITool, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: useTools.partialQueryKey() });
  },
});

export const listAIToolVersionsQuery = createQueryOptions(listAIToolVersions);

export const useUpdateTool = createMutationHook(patchAITool, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: useTools.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: getToolsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listAIToolVersionsQuery.partialQueryKey() });
  },
});

export const useUpdateToolUsagesBulk = createMutationHook(bulkVersionUpdateAITool, {
  onSuccess: async (data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey, toolKey } = variables;
    const results = data?.results ?? [];
    const configKeys = new Set<string>();
    for (const row of results) {
      if (row.configKey) {
        configKeys.add(row.configKey);
      }
    }
    const invalidationTasks = [
      queryClient.invalidateQueries({
        queryKey: listAIToolReferencesQuery.partialQueryKey({ projectKey, toolKey }),
      }),
    ];
    for (const configKey of configKeys) {
      invalidationTasks.push(
        queryClient.invalidateQueries({
          queryKey: getAiConfigQuery.partialQueryKey({ projectKey, configKey }),
        }),
      );
    }
    await Promise.all(invalidationTasks);
  },
});

// Evaluation providers/metadata
export const listEvaluationProvidersQuery = createQueryOptions(listAIEvaluationProviders);

// Evaluations - these hit the new ai-evaluator evaluations API, use these if you can, especially if you need bulk eval support
export const listEvaluationsQuery = createQueryOptions(listEvaluations);
export const getEvaluationQuery = createQueryOptions(getEvaluation);
export const useUpdateEvaluation = createMutationHook(patchEvaluation);
export const useCreateEvaluation = createMutationHook(postEvaluation);
export const listEvaluationRunsQuery = createQueryOptions(listEvaluationRuns);
export const getEvaluationRunQuery = createQueryOptions(getEvaluationRun);
export const useCreateRun = createMutationHook(createEvaluationRun);
export const listEvaluationRunResultsQuery = createQueryOptions(listEvaluationRunResults);
export const getEvaluationRunSummaryQuery = createQueryOptions(getEvaluationRunSummary);
export const listRunsQuery = createQueryOptions(listRuns);
export const previewDatasetRowsQuery = createQueryOptions(previewDatasetRows);
export const useCancelEvaluationRun = createMutationHook(cancelEvaluationRun);

export const listPlaygroundsQuery = createQueryOptions(listPlaygrounds);
export const listPlaygroundsInfiniteQuery = createInfiniteQueryOptions(listPlaygrounds);
export const getPlaygroundQuery = createQueryOptions(getPlayground);

export const useCreatePlayground = createMutationHook(postPlayground);

export const useUpdatePlayground = createMutationHook(patchPlayground, {
  onSuccess: async (_data, { projKey, playgroundId }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listPlaygroundsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listPlaygroundsInfiniteQuery.partialQueryKey() });
    await queryClient.invalidateQueries({
      queryKey: getPlaygroundQuery.partialQueryKey({ projKey, playgroundId }),
    });
  },
});
// Graphs
export const getAgentGraphQuery = createQueryOptions(getAgentGraph);
export const listAgentGraphsQuery = createQueryOptions(listAgentGraphs);

export const useCreateAgentGraph = createMutationHook(postAgentGraph, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listAgentGraphsQuery.partialQueryKey() });
  },
});

export const useUpdateAgentGraph = createMutationHook(patchAgentGraph, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAgentGraphQuery.partialQueryKey() });
  },
});

export const useDeleteAgentGraph = createMutationHook(deleteAgentGraph, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listAgentGraphsQuery.partialQueryKey() });
  },
});

// Agent Optimizations
export const getAgentOptimizationQuery = createQueryOptions(getAgentOptimization);
export const listAgentOptimizationsQuery = createQueryOptions(listAgentOptimizations);
export const listAgentOptimizationResultsQuery = createQueryOptions(listAgentOptimizationResults);
export const listAllAgentOptimizationResultsQuery = createQueryOptions(listAllAgentOptimizationResults);
export const listAgentOptimizationResultsByRunIdQuery = createQueryOptions(listAgentOptimizationResultsByRunId);
export const listAgentOptimizationRunsQuery = createQueryOptions(listAgentOptimizationRuns);

export const useCreateAgentOptimization = createMutationHook(postAgentOptimization, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listAgentOptimizationsQuery.partialQueryKey() });
  },
});

export const useUpdateAgentOptimization = createMutationHook(patchAgentOptimization, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getAgentOptimizationQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listAgentOptimizationsQuery.partialQueryKey() });
  },
});

export const useDeleteAgentOptimization = createMutationHook(deleteAgentOptimization, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listAgentOptimizationsQuery.partialQueryKey() });
  },
});

export const usePatchAgentOptimizationResult = createMutationHook(patchAgentOptimizationResult, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listAllAgentOptimizationResultsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listAgentOptimizationResultsByRunIdQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listAgentOptimizationRunsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: getAgentOptimizationQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: listAgentOptimizationsQuery.partialQueryKey() });
  },
});

// AI Tool References
export const listAIToolReferencesQuery = createQueryOptions(listAIToolReferences);

// AI Config References (e.g. which AI Configs use a given judge config)
export const internalListAIConfigReferencesQuery = createQueryOptions(internalListAIConfigReferences);
export const internalListAIConfigReferencesInfiniteQuery = createInfiniteQueryOptions(internalListAIConfigReferences);

// Prompt Snippets
export const listPromptSnippetsQuery = createQueryOptions(listPromptSnippets);
export const listPromptSnippetsInfiniteQuery = createInfiniteQueryOptions(listPromptSnippets);
export const listPromptSnippetReferencesQuery = createQueryOptions(listPromptSnippetReferences);
export const listPromptSnippetReferencesInfiniteQuery = createInfiniteQueryOptions(listPromptSnippetReferences);
export const listPromptSnippetVersionsQuery = createQueryOptions(listPromptSnippetVersions);
export const getPromptSnippetQuery = createQueryOptions(getPromptSnippet);

export const useCreatePromptSnippet = createMutationHook(postPromptSnippet, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey } = variables;
    await queryClient.invalidateQueries({ queryKey: listPromptSnippetsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: listPromptSnippetsInfiniteQuery.partialQueryKey({ projectKey }) });
  },
});

export const useUpdatePromptSnippet = createMutationHook(patchPromptSnippet, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey, snippetKey } = variables;
    await queryClient.invalidateQueries({ queryKey: listPromptSnippetsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: listPromptSnippetsInfiniteQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({
      queryKey: getPromptSnippetQuery.partialQueryKey({ projectKey, snippetKey }),
    });
    await queryClient.invalidateQueries({
      queryKey: listPromptSnippetVersionsQuery.partialQueryKey({ projectKey, snippetKey }),
    });
    await queryClient.invalidateQueries({
      queryKey: listPromptSnippetReferencesQuery.partialQueryKey({ projectKey, snippetKey }),
    });
    await queryClient.invalidateQueries({
      queryKey: listPromptSnippetReferencesInfiniteQuery.partialQueryKey({ projectKey, snippetKey }),
    });
  },
});

export const useDeletePromptSnippet = createMutationHook(deletePromptSnippet, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey } = variables;
    await queryClient.invalidateQueries({ queryKey: listPromptSnippetsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: listPromptSnippetsInfiniteQuery.partialQueryKey({ projectKey }) });
  },
});

export const useUpdateSnippetUsages = createMutationHook(bulkVersionUpdatePromptSnippet, {
  onSuccess: async (data, variables) => {
    const queryClient = getQueryClient();
    const refsPartial = {
      projectKey: variables.projectKey,
      snippetKey: variables.snippetKey,
    };
    const results = data?.results ?? [];
    const configKeys = new Set<string>();
    for (const row of results) {
      if (row.configKey) {
        configKeys.add(row.configKey);
      }
    }
    const invalidationTasks = [
      queryClient.invalidateQueries({ queryKey: listPromptSnippetReferencesQuery.partialQueryKey(refsPartial) }),
      queryClient.invalidateQueries({
        queryKey: listPromptSnippetReferencesInfiniteQuery.partialQueryKey(refsPartial),
      }),
    ];
    for (const configKey of configKeys) {
      invalidationTasks.push(
        queryClient.invalidateQueries({
          queryKey: getAiConfigQuery.partialQueryKey({ projectKey: variables.projectKey, configKey }),
        }),
      );
    }
    await Promise.all(invalidationTasks);
  },
});

// Agent Skills
export const listAgentSkillsQuery = createQueryOptions(listAgentSkills);
export const listAgentSkillsInfiniteQuery = createInfiniteQueryOptions(listAgentSkills);
export const getAgentSkillQuery = createQueryOptions(getAgentSkill);
export const listAgentSkillReferencesQuery = createQueryOptions(listAgentSkillReferences);
export const listAgentSkillReferencesInfiniteQuery = createInfiniteQueryOptions(listAgentSkillReferences);
export const listAgentSkillVersionsQuery = createQueryOptions(listAgentSkillVersions);

export const useCreateAgentSkill = createMutationHook(postAgentSkill, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey } = variables;
    await queryClient.invalidateQueries({ queryKey: listAgentSkillsQuery.partialQueryKey({ projectKey }) });
  },
});

export const useUpdateAgentSkill = createMutationHook(patchAgentSkill, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey, skillKey } = variables;
    await queryClient.invalidateQueries({ queryKey: listAgentSkillsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getAgentSkillQuery.partialQueryKey({ projectKey, skillKey }) });
    await queryClient.invalidateQueries({
      queryKey: listAgentSkillReferencesQuery.partialQueryKey({ projectKey, skillKey }),
    });
    await queryClient.invalidateQueries({
      queryKey: listAgentSkillReferencesInfiniteQuery.partialQueryKey({ projectKey, skillKey }),
    });
    await queryClient.invalidateQueries({
      queryKey: listAgentSkillVersionsQuery.partialQueryKey({ projectKey, skillKey }),
    });
  },
});

export const useDeleteAgentSkill = createMutationHook(deleteAgentSkill, {
  onSuccess: async (_data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey } = variables;
    await queryClient.invalidateQueries({ queryKey: listAgentSkillsQuery.partialQueryKey({ projectKey }) });
  },
});

export const useUpdateAgentSkillUsagesBulk = createMutationHook(bulkVersionUpdateAgentSkill, {
  onSuccess: async (data, variables) => {
    const queryClient = getQueryClient();
    const { projectKey, skillKey } = variables;
    const refsPartial = { projectKey, skillKey };
    const results = data?.results ?? [];
    const configKeys = new Set<string>();
    for (const row of results) {
      if (row.configKey) {
        configKeys.add(row.configKey);
      }
    }
    const invalidationTasks = [
      queryClient.invalidateQueries({ queryKey: listAgentSkillReferencesQuery.partialQueryKey(refsPartial) }),
      queryClient.invalidateQueries({ queryKey: listAgentSkillReferencesInfiniteQuery.partialQueryKey(refsPartial) }),
    ];
    for (const configKey of configKeys) {
      invalidationTasks.push(
        queryClient.invalidateQueries({ queryKey: getAiConfigQuery.partialQueryKey({ projectKey, configKey }) }),
      );
    }
    await Promise.all(invalidationTasks);
  },
});

export const listEvaluationsInfiniteQuery = createInfiniteQueryOptions(listEvaluations);
